import React, { useContext, useState } from 'react';
import { withRouter, Link, useLocation } from 'react-router-dom';
import ReactTable from 'react-table';
import ReactButton from 'components/ReactButton';
import Popup from 'components/Layout/Popup';
import Svg from 'components/Svg';
import BackButton from 'components/BackButton';
import AddBtnRedesigned from 'components/AddFiltersBtnRedesigned/AddBtnRedesigned';
import TablePaginator from 'components/TablePaginator';
import SelectWithValidation from 'components/SelectWithValidation';
import InputWithValidation from 'components/InputWithValidation';
import API from '../../../helpers/API';
import { composeShipmentFilterData } from './composeShipmentFilterData';
import styles from './ShipmentFilterList.scss';
import { headerStyle, style } from './styles';
import typography from '../../../helpers/appTypography';
import ShipmentFilterContext from './ShipmentFiltersContext';

const ShipmentFilterList = (props) => {
  const {
    shipmentEventFilters,
    availableShipmentStates,
    customEventTypes,
    availableCalculatedEvents,
    setShipmentFiltersUpdated,
  } = useContext(ShipmentFilterContext);

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useQuery();
  const queryFilterProperty = query.get('filter-property') ? query.get('filter-property') : 'name';
  const queryFilterValue = query.get('filter-value') ? query.get('filter-value') : '';

  const [selectedFilterProperty, setSelectedFilterProperty] = useState(queryFilterProperty);
  const [selectedFilterValue, setSelectedFilterValue] = useState(queryFilterValue);

  const filterByCalculatedEvents = (filter, newSelectedFilterValue) => {
    const foundEvent = availableCalculatedEvents.find(ce => ce.name.toLowerCase().includes(newSelectedFilterValue.toLowerCase()));
    if (foundEvent) {
      return filter.calculated_events && filter.calculated_events.includes(foundEvent.id);
    }
    return false;
  };

  const filterByCustomEvents = (filter, newSelectedFilterValue) => {
    const foundEvent = customEventTypes.find(cet => cet.name.toLowerCase().includes(newSelectedFilterValue.toLowerCase()));
    if (foundEvent) {
      return filter.custom_event_types && filter.custom_event_types.includes(foundEvent.id);
    }
    return false;
  };

  const getFilteredFilters = (newSelectedFilterProperty, newSelectedFilterValue) => {
    if (newSelectedFilterProperty && newSelectedFilterValue) {
      const filteredData = shipmentEventFilters.filter((filter) => {
        switch (newSelectedFilterProperty) {
          case 'name':
            return filter.name && filter.name.toLowerCase().includes(newSelectedFilterValue.toLowerCase());
          case 'tags':
            return filter.tags && filter.tags.some(tag => tag.toLowerCase().includes(newSelectedFilterValue.toLowerCase()));
          case 'shipment_types':
            return filter.shipment_types && filter.shipment_types.some(st => st.toLowerCase().includes(newSelectedFilterValue.toLowerCase()));
          case 'shipment_states':
            return filter.states && filter.states.some(state => state.name.toLowerCase().includes(newSelectedFilterValue.toLowerCase()));
          case 'calculated_events':
            return filterByCalculatedEvents(filter, newSelectedFilterValue);
          case 'custom_events':
            return filterByCustomEvents(filter, newSelectedFilterValue);
          default:
            return false;
        }
      });
      return filteredData.map((filter) => composeShipmentFilterData(availableShipmentStates, filter));
    }
    return shipmentEventFilters.map((filter) => composeShipmentFilterData(availableShipmentStates, filter));
  };

  const [composedShipmentFilterData, setComposedShipmentFilterData] = useState(getFilteredFilters(selectedFilterProperty, selectedFilterValue));

  const [popUpProperties, setPopUpProperties] = useState({
    title: '',
    description: '',
    onAccept: null,
    onDecline: null,
    open: false,
  });

  const customEventTypesSort = (cetIds1, cetIds2) => {
    const cetString1 = cetIds1.map(id => customEventTypes.find(cet => cet.id === id).name).join();
    const cetString2 = cetIds2.map(id => customEventTypes.find(cet => cet.id === id).name).join();
    return cetString1.localeCompare(cetString2);
  };

  const shipmentStatesSort = (states1, states2) => {
    const stateString1 = states1.map(state => state.name).join();
    const stateString2 = states2.map(state => state.name).join();
    return stateString1.localeCompare(stateString2);
  };

  const handleFilterInUseError = () => {
    setPopUpProperties({
      title: 'Shipment filter in use',
      description: 'Cannot complete operation as shipment filter is being used by one or more webhooks.',
      open: true,
      onDecline: () => setPopUpProperties({ open: false }),
    });
  };

  const handleDelete = (id) => {
    setPopUpProperties({
      title: 'Are you sure?',
      description: <span>Revoking of Shipment Filter <u>deletes</u> it completely.</span>,
      open: true,
      onAccept: () => API.deleteShipmentFilter(id)
        .then(() => {
          setPopUpProperties({ open: false });
          setShipmentFiltersUpdated(true);
        })
        .catch((err) => {
          if (err.status === 400) {
            handleFilterInUseError();
            return Promise.resolve();
          }
          return Promise.reject(err.json);
        }),
      onDecline: () => {
        setPopUpProperties({ open: false });
      },
    });
  };

  const renderCellText = (text) => (
    <div className={styles.tableCellText}>
      {text}
    </div>
  );

  const renderArrayCell = (array) => (
    array && array.length > 0 ? (renderCellText(array.join(', '))) : ''
  );

  const renderObjectsCell = (objectArr, availableNamesToObjectArr) => (
    objectArr && objectArr.length > 0 ? (renderCellText(objectArr.map(item => (availableNamesToObjectArr.find(x => x.id === item).name)).join(', '))) : ''
  );

  const renderStates = (states) => (
    states && states.length > 0 ? (renderCellText(states.map((state) => state.name).join(', '))) : ''
  );

  const renderActions = (cellProps) => (
    <div className={styles.tableButtonsBlock}>
      <span className={styles.actionsBtnBlock} title="Edit">
        <Link to={`/settings/shipment-filters/${cellProps.original.id}`}>
          <Svg name="TableEditUser" />
        </Link>
      </span>
      <span className={styles.actionsBtnBlock} title="Delete">
        <Svg
          name="TableDeleteUser"
          onClick={() => handleDelete(cellProps.original.id)}
          className={styles.btnDelete}
        />
      </span>
    </div>
  );

  const getTable = () => (
    <>
      <div className={styles.tableControls}>
        <SelectWithValidation
          label="Filter by"
          placeholder="Filter property"
          name="filter_property"
          options={[
            { label: 'Name', value: 'name' },
            { label: 'Shipment Tags', value: 'tags' },
            { label: 'Shipment Types', value: 'shipment_types' },
            { label: 'Shipment States', value: 'shipment_states' },
            { label: 'Calculated Events', value: 'calculated_events' },
            { label: 'Custom Events', value: 'custom_events' },
          ]}
          onChange={(e) => {
            setSelectedFilterProperty(e.value);
          }}
          value={selectedFilterProperty}
          id="filter-options"
        />
        <InputWithValidation
          placeholder="Filter value"
          name="filter_value"
          onChange={(e) => {
            setSelectedFilterValue(e.target.value);
          }}
          value={selectedFilterValue}
          id="filter-value"
        />
        <ReactButton
          className={styles.button}
          onClick={() => {
            setComposedShipmentFilterData(getFilteredFilters(selectedFilterProperty, selectedFilterValue));
            if (selectedFilterProperty !== '' && selectedFilterValue !== '') {
              props.history.push(`?filter-property=${selectedFilterProperty}&filter-value=${selectedFilterValue}`);
            } else {
              props.history.push('/settings/shipment-filters');
            }
          }}
        >
          Filter
        </ReactButton>
        <ReactButton
          className={styles.button}
          onClick={() => {
            setSelectedFilterProperty('name');
            setSelectedFilterValue('');
            setComposedShipmentFilterData(getFilteredFilters('', ''));
            props.history.push('/settings/shipment-filters');
          }}
        >
          Clear
        </ReactButton>
      </div>
      <ReactTable
        data={composedShipmentFilterData}
        minRows={0}
        columns={[
          {
            Header: 'Name',
            accessor: 'name',
            Cell: cellProps => renderCellText(cellProps.original.name),
            headerStyle,
            style,
            width: 350,
          },
          {
            Header: 'Shipment Tags',
            accessor: 'tags',
            Cell: cellProps => renderArrayCell(cellProps.original.tags),
            headerStyle,
            style,
          },
          {
            Header: 'Shipment Types',
            accessor: 'shipment_types',
            Cell: cellProps => renderArrayCell(cellProps.original.shipment_types),
            headerStyle,
            style,
          },
          {
            Header: 'Shipment States',
            accessor: 'states',
            Cell: cellProps => renderStates(cellProps.original.states),
            headerStyle,
            style,
            sortMethod: shipmentStatesSort,
          },
          {
            Header: 'Calculated Events',
            accessor: 'calculated_events',
            Cell: cellProps => renderObjectsCell(cellProps.original.calculated_events, availableCalculatedEvents),
            headerStyle,
            style,
          },
          {
            Header: 'Custom Events',
            accessor: 'custom_event_types',
            Cell: cellProps => renderObjectsCell(cellProps.original.custom_event_types, customEventTypes),
            headerStyle,
            style,
            sortMethod: customEventTypesSort,
          },
          {
            Header: 'Actions',
            accessor: 'actions',
            Cell: cellProps => renderActions(cellProps),
            width: 250,
            headerStyle,
            style,
            sortable: false,
          },
        ]}
        sortable
        defaultSorted={[
          {
            id: 'name',
            desc: false,
          },
        ]}
        showPageSizeOptions={false}
        resizable={false}
        howPageJump={false}
        getTrProps={() => ({
          style: { border: 'none' },
        })}
        PaginationComponent={TablePaginator}
        footerText="shipment event filters"
        pageSizeOptions={[20, 50, 100, 200]}
        totalRows={composedShipmentFilterData.length}
      />
    </>
  );

  return (
    <div>
      <Popup {...popUpProperties} />
      <div className={styles.titleBlock}>
        <div>
          <BackButton onBack={() => window.history.back()} />
          <h1 className="h2" style={{ margin: 0 }}>{typography.shipmentFilters.title}</h1>
          <div style={{ margin: '0 0 20px 0' }}>{typography.shipmentFilters.subTitle}</div>
        </div>
        <div className={styles.btnAdditionalStyles}>
          <Link to="/settings/shipment-filters/create">
            <AddBtnRedesigned>
              + Add shipment event filter
            </AddBtnRedesigned>
          </Link>
        </div>
      </div>
      <div className={styles.outerWhiteContainer}>
        { shipmentEventFilters && shipmentEventFilters.length > 0 && getTable() }
      </div>
    </div>
  );
};

export default withRouter(ShipmentFilterList);
